import { useState } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface AsyncButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  // Sync or async click handler. If it returns a promise, the button shows a spinner
  // and disables itself until it settles.
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;
  // External loading flag (e.g. a react-query isFetching) — ORs with the internal one.
  loading?: boolean;
  // FontAwesome icon class shown when not loading (me-1 only when there's a label).
  icon?: string;
  children?: ReactNode;
}

// Button that always shows a loader while its action is in flight — the shared
// implementation of the "every button shows a loader on click" rule. For query-driven
// buttons pass `loading`; for async handlers the spinner is automatic.
export function AsyncButton({ onClick, loading, icon, children, disabled, className = 'btn btn-primary btn-sm', ...rest }: AsyncButtonProps) {
  const [pending, setPending] = useState(false);
  const busy = Boolean(loading) || pending;

  const handle = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClick || busy) return;
    const result = onClick(e);
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      setPending(true);
      try {
        await result;
      } finally {
        setPending(false);
      }
    }
  };

  return (
    <button {...rest} type={rest.type ?? 'button'} className={className} disabled={disabled || busy} onClick={handle}>
      {busy ? <i className={`fa fa-spinner fa-spin${children ? ' me-1' : ''}`} /> : icon ? <i className={`${icon}${children ? ' me-1' : ''}`} /> : null}
      {children}
    </button>
  );
}
